import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2, MoreHorizontal, Pencil, RefreshCw, Save, Send, Trash2, Workflow } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader } from '@/core/components/ui/card';
import { Button } from '@/core/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/core/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/core/components/ui/tooltip';
import { hasPendingRecord, isFlowDirty, PENDING_RECORD_REASON, useKioskFlowStore } from '../../stores/kiosk-flow-store';
import type { KioskFlow } from '../../types/kiosk-flow-types';
import { FlowNameDialog } from '../molecules/FlowNameDialog';
import { DeleteFlowDialog } from '../molecules/DeleteFlowDialog';
import { FlowStepsTable } from '../molecules/FlowStepsTable';
import { PublishFlowDialog } from '../molecules/PublishFlowDialog';
import { PublishValidationErrors } from '../molecules/PublishValidationErrors';
import { DismissUnrecordedDialog } from '../molecules/DismissUnrecordedDialog';
import { BannerSelect } from '../molecules/BannerSelect';
import { FlowValidationSummary } from '../molecules/FlowValidationSummary';
import { formatDraftIssue, validateDraft } from '../../utils/flow-steps';

function formatDate(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleString();
}

interface FlowEditorProps {
  flow: KioskFlow;
  operatorName: string;
}

// Editor for the operator's flow — header with save/publish and rename/delete, then the steps table
export function FlowEditor({ flow, operatorName }: FlowEditorProps) {
  const isRenamingFlow = useKioskFlowStore((s) => s.isRenamingFlow);
  const isDeletingFlow = useKioskFlowStore((s) => s.isDeletingFlow);
  const renameFlowError = useKioskFlowStore((s) => s.renameFlowError);
  const deleteFlowError = useKioskFlowStore((s) => s.deleteFlowError);
  const renameFlow = useKioskFlowStore((s) => s.renameFlow);
  const deleteFlow = useKioskFlowStore((s) => s.deleteFlow);
  const clearRenameFlowError = useKioskFlowStore((s) => s.clearRenameFlowError);
  const clearDeleteFlowError = useKioskFlowStore((s) => s.clearDeleteFlowError);
  const draftSteps = useKioskFlowStore((s) => s.draftSteps);
  const isSavingFlow = useKioskFlowStore((s) => s.isSavingFlow);
  const saveFlowError = useKioskFlowStore((s) => s.saveFlowError);
  const layouts = useKioskFlowStore((s) => s.layouts);
  const isLoadingLayouts = useKioskFlowStore((s) => s.isLoadingLayouts);
  const layoutsError = useKioskFlowStore((s) => s.layoutsError);
  const addStep = useKioskFlowStore((s) => s.addStep);
  const updateStep = useKioskFlowStore((s) => s.updateStep);
  const removeStep = useKioskFlowStore((s) => s.removeStep);
  const moveStep = useKioskFlowStore((s) => s.moveStep);
  const saveFlow = useKioskFlowStore((s) => s.saveFlow);
  const loadLayouts = useKioskFlowStore((s) => s.loadLayouts);
  const draftBanner = useKioskFlowStore((s) => s.draftBanner);
  const setDraftBanner = useKioskFlowStore((s) => s.setDraftBanner);
  const images = useKioskFlowStore((s) => s.images);
  const isLoadingImages = useKioskFlowStore((s) => s.isLoadingImages);
  const imagesError = useKioskFlowStore((s) => s.imagesError);
  const loadImages = useKioskFlowStore((s) => s.loadImages);
  const isDirty = useKioskFlowStore(isFlowDirty);
  const isPublishing = useKioskFlowStore((s) => s.isPublishing);
  const isRecording = useKioskFlowStore((s) => s.isRecording);
  const publishError = useKioskFlowStore((s) => s.publishError);
  const publishValidationErrors = useKioskFlowStore((s) => s.publishValidationErrors);
  const unrecordedJson = useKioskFlowStore((s) => s.unrecordedJson);
  const recordConflict = useKioskFlowStore((s) => s.recordConflict);
  const pendingRecord = useKioskFlowStore(hasPendingRecord);
  const publishFlow = useKioskFlowStore((s) => s.publishFlow);
  const retryRecord = useKioskFlowStore((s) => s.retryRecord);
  const dismissUnrecorded = useKioskFlowStore((s) => s.dismissUnrecorded);
  const previewPublish = useKioskFlowStore((s) => s.previewPublish);

  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [publishOpen, setPublishOpen] = useState(false);
  const [dismissOpen, setDismissOpen] = useState(false);

  const handleRename = async (name: string) => {
    if (await renameFlow(name)) {
      setRenameOpen(false);
      toast.success(`Flow renamed to "${name.trim()}"`);
    }
  };

  // Asset Manager files are only checked once their list has loaded.
  // An empty flow skips the required-step issues — Publish already says "Add steps to the flow first".
  const issues = useMemo(() => {
    const all = validateDraft(draftSteps, draftBanner, {
      layouts: isLoadingLayouts || layoutsError ? null : layouts,
      images: isLoadingImages || imagesError ? null : images,
    });
    return draftSteps.length === 0 ? all.filter((i) => i.field !== 'screenFlow') : all;
  }, [draftSteps, draftBanner, layouts, isLoadingLayouts, layoutsError, images, isLoadingImages, imagesError]);
  const saveIssues = issues.filter((i) => i.blocks === 'save');
  const bannerIssue = issues.find((i) => i.field === 'promoBannerFileName');
  const isBusy = isSavingFlow || isPublishing || isRecording;
  const canSave = isDirty && saveIssues.length === 0 && !isBusy;
  // A new publish would replace the JSON Retry record still needs.
  // Publish works on the saved draft, so unsaved edits block it.
  const firstIssue = saveIssues[0] ?? issues[0];
  const publishBlockedReason = pendingRecord
    ? PENDING_RECORD_REASON
    : isDirty
      ? 'Save your changes first'
      : draftSteps.length === 0
        ? 'Add steps to the flow first'
        : firstIssue
          ? formatDraftIssue(firstIssue)
          : null;
  const canPublish = !publishBlockedReason && !isBusy;

  // Reloading or closing the tab mid-publish leaves the kiosks in an unknown state,
  // with a record pending it drops the JSON Retry record needs, and unsaved edits would be lost.
  // In-app links aren't blocked — the app uses BrowserRouter, which has no navigation blocker.
  const guardUnload = isPublishing || pendingRecord || isDirty;
  useEffect(() => {
    if (!guardUnload) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [guardUnload]);

  const handleSave = async () => {
    if (await saveFlow()) toast.success('Flow saved');
  };

  const handlePublish = async () => {
    setPublishOpen(false);
    const version = await publishFlow();
    if (version) {
      toast.success(`Flow published — version ${version.version}`);
      return;
    }
    // Record failures show as a banner with Retry. A prepare failure never reached the kiosks; a push
    // failure might have (e.g. the response timed out after the Kiosk API saved), so don't claim either way
    const state = useKioskFlowStore.getState();
    if (!state.publishError || state.unrecordedJson) return;
    toast.error(
      state.publishValidationErrors.length > 0
        ? "The flow can't be published — see the errors below"
        : state.publishFailedStage === 'push'
          ? `Publish failed, the kiosks may not have been updated: ${state.publishError}`
          : `Publish failed, the kiosks were not updated: ${state.publishError}`
    );
  };

  const handleRetryRecord = async () => {
    const version = await retryRecord();
    if (version) toast.success(`Flow published — version ${version.version}`);
  };

  const handleDelete = async () => {
    const flowName = flow.name;
    if (await deleteFlow()) {
      setDeleteOpen(false);
      toast.success(`Flow "${flowName}" deleted`);
    }
  };

  const updatedAt = formatDate(flow.updatedAt);
  const publishedAt = formatDate(flow.lastPublishedAt);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Workflow className="h-5 w-5 text-yellow-700" />
            </div>
            <div>
              <p className="font-semibold text-gray-900">{flow.name}</p>
              <div className="space-y-0.5 text-xs text-gray-500">
                {updatedAt && (
                  <p>
                    Last saved {updatedAt}
                    {flow.updatedByName ? ` by ${flow.updatedByName}` : ''}
                  </p>
                )}
                <p>
                  {publishedAt
                    ? `Last published${flow.lastPublishedByName ? ` by ${flow.lastPublishedByName}` : ''} at ${publishedAt}`
                    : 'Not published yet'}
                </p>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {isDirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
            <Button size="sm" variant="outline" onClick={handleSave} disabled={!canSave}>
              {isSavingFlow ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Save
            </Button>
            <TooltipProvider>
              <Tooltip delayDuration={200}>
                <TooltipTrigger asChild>
                  {/* span so the tooltip still shows while the button is disabled */}
                  <span tabIndex={publishBlockedReason ? 0 : -1}>
                    <Button size="sm" onClick={() => setPublishOpen(true)} disabled={!canPublish}>
                      {isPublishing ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="mr-2 h-4 w-4" />
                      )}
                      {isPublishing ? 'Publishing…' : 'Publish'}
                    </Button>
                  </span>
                </TooltipTrigger>
                {publishBlockedReason && <TooltipContent>{publishBlockedReason}</TooltipContent>}
              </Tooltip>
            </TooltipProvider>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Flow actions" disabled={isBusy}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Rename
                </DropdownMenuItem>
                {/* Deleting would drop the JSON Retry record needs */}
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600"
                  disabled={pendingRecord}
                  onSelect={() => setDeleteOpen(true)}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </DropdownMenuItem>
                {pendingRecord && <p className="max-w-56 px-2 pb-1.5 text-xs text-gray-500">{PENDING_RECORD_REASON}</p>}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <FlowValidationSummary issues={issues} />
          {unrecordedJson && (
            <div className="flex items-start gap-3 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div className="flex-1 space-y-1">
                <p className="font-medium">Kiosks were updated but the version wasn't recorded</p>
                <p>
                  {recordConflict
                    ? 'The flow changed after it was sent to the kiosks. Publish again so the history matches what the kiosks run.'
                    : publishError}
                </p>
              </div>
              <div className="flex shrink-0 gap-2">
                <Button size="sm" variant="ghost" onClick={() => setDismissOpen(true)} disabled={isRecording}>
                  Dismiss
                </Button>
                {/* On a conflict retrying can't work — the user publishes again or dismisses */}
                {!recordConflict && (
                  <Button size="sm" variant="outline" onClick={handleRetryRecord} disabled={isRecording}>
                    {isRecording ? (
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="mr-2 h-3 w-3" />
                    )}
                    Retry record
                  </Button>
                )}
              </div>
            </div>
          )}
          <BannerSelect
            value={draftBanner}
            images={images}
            isLoading={isLoadingImages}
            error={imagesError}
            issue={bannerIssue}
            disabled={isBusy}
            onChange={setDraftBanner}
            onRetry={loadImages}
          />
          <FlowStepsTable
            steps={draftSteps}
            layouts={layouts}
            isLoadingLayouts={isLoadingLayouts}
            layoutsError={layoutsError}
            issues={issues}
            disabled={isBusy}
            onAdd={addStep}
            onChange={updateStep}
            onRemove={removeStep}
            onMove={moveStep}
            onRetryLayouts={loadLayouts}
          />
          {saveFlowError && <p className="text-sm text-red-600">{saveFlowError}</p>}
          <PublishValidationErrors errors={publishValidationErrors} />
        </CardContent>
      </Card>

      <FlowNameDialog
        open={renameOpen}
        mode="rename"
        operatorName={operatorName}
        initialName={flow.name}
        isSaving={isRenamingFlow}
        error={renameFlowError}
        onOpenChange={(open) => {
          if (!open) clearRenameFlowError();
          setRenameOpen(open);
        }}
        onSubmit={handleRename}
      />
      <PublishFlowDialog
        open={publishOpen}
        operatorName={operatorName}
        onOpenChange={setPublishOpen}
        onConfirm={handlePublish}
        loadPreview={previewPublish}
      />
      <DismissUnrecordedDialog
        open={dismissOpen}
        onOpenChange={setDismissOpen}
        onConfirm={() => {
          dismissUnrecorded();
          setDismissOpen(false);
        }}
      />
      <DeleteFlowDialog
        open={deleteOpen}
        flowName={flow.name}
        isDeleting={isDeletingFlow}
        error={deleteFlowError}
        onOpenChange={(open) => {
          if (!open) clearDeleteFlowError();
          setDeleteOpen(open);
        }}
        onConfirm={handleDelete}
      />
    </>
  );
}
